import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useGetManagerGroupQuery } from '../../api/groupApi';
import { useGetGroupActiveSubscribersQuery } from '../../api/metricsApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import { rangeFromSearchParams, buildRangeQuery } from '../../utils/dateRange';
import BackHeader from '../../components/BackHeader/BackHeader';
import Badge from '../../components/Badge/Badge';
import DeliveryStatsCell from '../../components/DeliveryStatsCell/DeliveryStatsCell';
import s from '../../styles/shared/page.module.css';
import table from '../../styles/shared/table.module.css';

function buildSubscriberDeliveriesUrl(groupId, subscriberId, range) {
  return `/manager/metrics/groups/${groupId}/subscribers/${subscriberId}/deliveries?${buildRangeQuery(range)}`;
}

export default function MetricsGroupSubscribersPage() {
  const { id: groupId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const showToast = useToast();

  const range = rangeFromSearchParams(searchParams);

  const { data: group } = useGetManagerGroupQuery(groupId);
  const {
    data,
    isLoading,
    isError,
    error,
  } = useGetGroupActiveSubscribersQuery({ groupId, range });

  useEffect(() => {
    if (isError) showToast('error', 'Subscribers', errMsg(error, 'Could not load subscribers.'));
  }, [isError, error, showToast]);

  const rows = data?.subscribers ?? [];

  return (
    <div className={s.page}>
      <BackHeader to="/manager/metrics" label="Back to Metrics" title={`Subscribers${group ? ` — ${group.name}` : ''}`} />

      <div className={table.tableWrapper}>
        <DataTable
          value={rows}
          loading={isLoading}
          emptyMessage="No active subscribers."
          responsiveLayout="stack"
        >
          <Column
            header="Subscriber"
            className={table.contactCol}
            body={(row) => <div className={table.cellInsider}>{row.subscriber.email}</div>}
          />
          <Column
            header="Channels"
            className={`${s.mutedCell} ${table.contactCol}`}
            body={(row) => <div className={table.cellInsider}>{row.channels.filter((c) => c !== 'push').join(', ')}</div>}
          />
          <Column
            header="Phone"
            className={s.mutedCell}
            body={(row) => <div className={table.cellInsider}>{row.subscriber.phone || '—'}</div>}
          />
          <Column
            header="Status"
            body={(row) => (
              <div className={table.cellInsider}>
                <div className={s.subscriberCell}>
                  <Badge
                    background={row.subscriber.email_verified ? 'var(--color-badge-info-bg)' : 'var(--color-badge-neutral-bg)'}
                    color={row.subscriber.email_verified ? 'var(--color-success)' : 'var(--color-text-muted)'}
                  >
                    {row.subscriber.email_verified ? 'verified' : 'unverified'}
                  </Badge>
                  {row.subscriber.is_external && (
                    <Badge background="var(--color-badge-warn-bg)" color="var(--color-badge-warn-text)">
                      external
                    </Badge>
                  )}
                </div>
              </div>
            )}
          />
          <Column
            header="Subscribed"
            className={s.mutedCell}
            body={(row) => <div className={table.cellInsider}>{new Date(row.subscribed_at).toLocaleDateString()}</div>}
          />
          <Column
            header="Messages"
            body={(row) => (
              <DeliveryStatsCell
                sent={row.stats.sent}
                delivered={row.stats.delivered}
                failed={row.stats.failed}
                hasDetails={row.stats.sent > 0}
                detailsLabel="Message details"
                onOpenDetails={() => navigate(buildSubscriberDeliveriesUrl(groupId, row.subscriber.id, range))}
              />
            )}
          />
        </DataTable>
      </div>
    </div>
  );
}
